import { READBACK_GLOSSES } from 'app/services/formula/operatorGlosses';
import LexicalParser from 'app/services/formula/parser/lexicalParser';
import SyntaxParser, { syntaxParser } from 'app/services/formula/parser/syntaxParser';
import { prettifyName } from 'app/services/formula/suggestionEngine';
import _ from 'lodash';

import { LEX_TOKEN_TYPES, SWITCH_FUNCTION_ENUM } from 'constants/parser';
import { getUUID } from 'utils/uuid';

import {
  AGGREGATE_FUNCTIONS,
  COLLECTION_PREDICATE_FUNCTIONS,
  COMPARISON_OPERATORS,
  GROUP_OPERATORS,
  OUTLINE_BADGE,
} from './expressionOutline.constants';

const OUTLINE_KIND = {
  GROUP: 'group',
  COLLECTION: 'collection',
  AGGREGATE: 'aggregate',
  SWITCH: 'switch',
  CASE: 'case',
  COMPARISON: 'comparison',
  RAW: 'raw',
};

const AGGREGATE_LABEL = {
  COUNT: collection => `Number of ${collection}`.trim(),
  SUM: (collection, field) => `Total of ${field} of ${collection}`.trim(),
  MIN_BY: (collection, field) => `Earliest ${field} of:`.trim(),
  MAX_BY: (collection, field) => `Latest ${field} of:`.trim(),
};

const prettifyPath = path => {
  if (!path) {
    return path;
  }

  return path
    .split('.')
    .map(segment => prettifyName(segment))
    .join(' › ');
};

const safeLabel = value => {
  if (_.isNil(value)) {
    return '';
  }

  return String(value);
};

const glossOperator = operator => READBACK_GLOSSES[operator] ?? operator;

const getArrayOperand = operands => operands.find(op => op.typeOfValue === LEX_TOKEN_TYPES.array);

const getPredicateOperands = (operands, arrayOperand) =>
  operands.filter(op => op.typeOfValue !== 'iterator' && op !== arrayOperand);

/**
 * Detect the always-true predicate a SWITCH uses for its default/else case
 * (`1 == 1`, `true`, or any `x == x`). The default case should read "Otherwise"
 * rather than "When 1 == 1".
 *
 * @param {Object|undefined} node
 * @returns {boolean}
 */
const isDefaultPredicate = node => {
  if (!node) {
    return false;
  }

  const value = safeLabel(node.value).toLowerCase();

  if (value === 'true') {
    return true;
  }

  if (!COMPARISON_OPERATORS.has(node.value) || node.value !== '==') {
    return false;
  }

  const operands = node.operands || [];
  const left = operands[0] ? safeLabel(operands[0].value) : null;
  const right = operands[1] ? safeLabel(operands[1].value) : null;

  return !_.isNil(left) && left === right;
};

const summarisePredicate = node => {
  if (!node) {
    return '';
  }

  const { value } = node;
  const operands = node.operands || [];

  if (COMPARISON_OPERATORS.has(value)) {
    const left = operands[0] ? prettifyPath(safeLabel(operands[0].value)) : '';
    const right = operands[1] ? safeLabel(operands[1].value) : '';

    return [left, glossOperator(value), right].filter(Boolean).join(' ');
  }

  if (GROUP_OPERATORS.has(value)) {
    const conjunction = value === 'AND' ? ' and ' : ' or ';

    return operands.map(summarisePredicate).filter(Boolean).join(conjunction);
  }

  return safeLabel(value);
};

const walkAst = (node, depth) => {
  if (!node) {
    return null;
  }

  const nodeValue = node.value;
  const operands = node.operands || [];

  if (node.type !== LEX_TOKEN_TYPES.function && node.type !== LEX_TOKEN_TYPES.operator) {
    return null;
  }

  if (GROUP_OPERATORS.has(nodeValue)) {
    const childNodes = operands.map(child => walkAst(child, depth + 1)).filter(Boolean);
    const childCount = operands.length;
    const isAll = nodeValue === 'AND';

    return {
      id: getUUID(),
      kind: OUTLINE_KIND.GROUP,
      badge: isAll ? OUTLINE_BADGE.ALL : OUTLINE_BADGE.ANY,
      label: isAll ? `Match ALL of these (${childCount})` : `Match ANY of these (${childCount})`,
      start: node.position?.start ?? null,
      end: node.position?.end ?? null,
      depth,
      children: childNodes,
    };
  }

  if (AGGREGATE_FUNCTIONS.has(nodeValue)) {
    const arrayOperand = getArrayOperand(operands);
    const arrayPath = arrayOperand?.value ?? operands[0]?.value ?? '';
    const collection = prettifyPath(arrayPath);
    const predicateOperands = getPredicateOperands(operands, arrayOperand);
    const fieldOperand = operands.find(
      op => op.typeOfValue === LEX_TOKEN_TYPES.field || op.type === LEX_TOKEN_TYPES.field,
    );
    const fieldLabel = fieldOperand ? prettifyPath(safeLabel(fieldOperand.value)) : '';
    const buildLabel = AGGREGATE_LABEL[nodeValue];
    const jumpPosition = arrayOperand?.position ?? node.position;

    return {
      id: getUUID(),
      kind: OUTLINE_KIND.AGGREGATE,
      badge: OUTLINE_BADGE.CALC,
      label: buildLabel ? buildLabel(collection, fieldLabel) : safeLabel(nodeValue),
      start: jumpPosition?.start ?? null,
      end: jumpPosition?.end ?? null,
      depth,
      children: predicateOperands.map(child => walkAst(child, depth + 1)).filter(Boolean),
    };
  }

  if (COLLECTION_PREDICATE_FUNCTIONS.has(nodeValue)) {
    const arrayOperand = getArrayOperand(operands);
    const arrayPath = arrayOperand?.value ?? operands[0]?.value ?? '';
    const collection = prettifyPath(arrayPath);
    const predicateOperands = getPredicateOperands(operands, arrayOperand);
    const predicateSummary = predicateOperands
      .map(summarisePredicate)
      .filter(Boolean)
      .join(' and ');
    const jumpPosition = arrayOperand?.position ?? node.position;
    const label = predicateSummary
      ? `each ${collection} where ${predicateSummary}`
      : `each ${collection}`;

    return {
      id: getUUID(),
      kind: OUTLINE_KIND.COLLECTION,
      badge: OUTLINE_BADGE.WHERE,
      label,
      start: jumpPosition?.start ?? null,
      end: jumpPosition?.end ?? null,
      depth,
      children: predicateOperands.map(child => walkAst(child, depth + 1)).filter(Boolean),
    };
  }

  if (nodeValue === SWITCH_FUNCTION_ENUM.SWITCH) {
    return {
      id: getUUID(),
      kind: OUTLINE_KIND.SWITCH,
      badge: OUTLINE_BADGE.IF,
      label: 'Choose a value by case',
      start: node.position?.start ?? null,
      end: node.position?.end ?? null,
      depth,
      children: operands.map(child => walkAst(child, depth + 1)).filter(Boolean),
    };
  }

  if (nodeValue === SWITCH_FUNCTION_ENUM.CASE) {
    const condition = operands[0];
    const conditionLabel = summarisePredicate(condition);

    const resolveLabel = () => {
      if (isDefaultPredicate(condition)) {
        return 'Otherwise';
      }

      return conditionLabel ? `When ${conditionLabel}` : 'When';
    };

    return {
      id: getUUID(),
      kind: OUTLINE_KIND.CASE,
      badge: OUTLINE_BADGE.IF,
      label: resolveLabel(),
      start: node.position?.start ?? null,
      end: node.position?.end ?? null,
      depth,
      children: operands.map(child => walkAst(child, depth + 1)).filter(Boolean),
    };
  }

  if (COMPARISON_OPERATORS.has(nodeValue)) {
    const left = operands[0];
    const right = operands[1];
    const leftLabel = left ? prettifyPath(safeLabel(left.value)) : '';
    const rightLabel = right ? safeLabel(right.value) : '';
    const label = [leftLabel, glossOperator(nodeValue), rightLabel].filter(Boolean).join(' ');

    return {
      id: getUUID(),
      kind: OUTLINE_KIND.COMPARISON,
      badge: null,
      label,
      start: left?.position?.start ?? node.position?.start ?? null,
      end: right?.position?.end ?? node.position?.end ?? null,
      depth,
      children: [],
    };
  }

  return {
    id: getUUID(),
    kind: OUTLINE_KIND.RAW,
    badge: null,
    label: safeLabel(nodeValue),
    start: node.position?.start ?? null,
    end: node.position?.end ?? null,
    depth,
    children: operands.map(child => walkAst(child, depth + 1)).filter(Boolean),
  };
};

const hasNestedContent = nodes => {
  if (!nodes || nodes.length === 0) {
    return false;
  }

  return nodes.some(node => node.depth > 0 || node.children.length > 0);
};

const parseAst = ({ tokens, schemaParams, isNewExpressionsParsingLogicEnabled }) => {
  if (isNewExpressionsParsingLogicEnabled) {
    const parser = new SyntaxParser(schemaParams);
    const result = parser.parse({ tokens, isOnlyBooleanAllowed: false });

    return result?.data ?? null;
  }

  const result = syntaxParser({ tokens, schemaParams, isOnlyBooleanAllowed: false });

  return result?.data ?? null;
};

export const buildOutline = ({ text, schemaParams, isNewExpressionsParsingLogicEnabled }) => {
  if (!text || !text.trim()) {
    return [];
  }

  if (_.isEmpty(schemaParams)) {
    return [];
  }

  try {
    const lexicalParser = new LexicalParser(schemaParams);
    const tokens = lexicalParser.parse(text);

    if (!tokens.length) {
      return [];
    }

    const astData = parseAst({ tokens, schemaParams, isNewExpressionsParsingLogicEnabled });

    if (!astData) {
      return [];
    }

    const rootNode = walkAst(astData, 0);

    if (!rootNode) {
      return [];
    }

    const nodes = [rootNode];

    if (!hasNestedContent(nodes)) {
      return [];
    }

    return nodes;
  } catch {
    return [];
  }
};
